import React from "react";
import { Box, HStack, Icon, Spinner, Text, Tooltip, VStack } from "@chakra-ui/react";
import { ArtifactProto } from "build-ts/chat_session";
import { FileType } from "build-ts/common";
import { AppIcons } from "constants/icons";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import ArtifactPanelHeader from "modules/artifacts/ArtifactPanelHeader";
import { HeaderWithStats } from "services/MetadataServices";
import { ScheduledJobProto } from "build-ts/schedule";

export interface WorkflowRunSearchCardProps {
  artifact: ArtifactProto;
  defaultTimeZone?: string;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  /** ID of the run currently being fetched — drives the per-row spinner. */
  loadingRunId?: string | null;
  /** Called when the user clicks a run row. Container owns the async fetch. */
  onRunClick?: (runId: string) => void;
  /** Analyst list, for analyst name lookup */
  analystList?: HeaderWithStats[];
  /** Scenario jobs, for owner ID lookup */
  scenarioJobs?: ScheduledJobProto[];
  /** Map of runId → author display name, resolved from source_chat_id. */
  runAuthorNames?: Record<string, string>;
}

/**
 * Pure UI component for WorkflowRunSearch artifacts.
 * All async logic (populateArtifact, loading state) lives in containers/WorkflowRunSearchCard.
 */
const WorkflowRunSearchCard: React.FC<WorkflowRunSearchCardProps> = ({
  artifact,
  defaultTimeZone = "",
  isExpanded,
  onExpandToggle,
  loadingRunId = null,
  onRunClick,
  analystList,
  scenarioJobs,
  runAuthorNames,
}) => {
  const search = artifact.workflow_run_search;
  const runs = search?.workflow_runs ?? [];
  const hasDateRange = search?.start_time || search?.end_time;
  // Guard against empty/undefined timezone — ZoneId.of("") throws
  const safeTimeZone = defaultTimeZone || undefined;
  const startDate =
    search?.start_time && safeTimeZone
      ? epochConverterWithTimeZone(Number(search.start_time), "d MMM yyyy", safeTimeZone)
      : undefined;
  const endDate =
    search?.end_time && safeTimeZone
      ? epochConverterWithTimeZone(Number(search.end_time), "d MMM yyyy", safeTimeZone)
      : undefined;
  const lastN = search?.last_n;

  return (
    <Box w="100%" h="100%" display="flex" flexDirection="column" bg="white" overflow="hidden">
      {/* ── Header ── */}
      <ArtifactPanelHeader
        icon={AppIcons.search}
        iconBg="white"
        iconColor="purple.700"
        name={search?.header?.name ?? "Run Search"}
        typeLabel="Run Search"
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      {/* ── Query context + run count ── */}
      <HStack px={3} py={1.5} flexShrink={0} justify="space-between" align="center" spacing={3}>
        {/* Left — count */}
        <Text fontSize="xs" color="gray.500" fontWeight="500" mb={0} flexShrink={0}>
          {runs.length} run{runs.length !== 1 ? "s" : ""} found
        </Text>

        {/* Right — date range + last N */}
        <VStack spacing={0} align="flex-end" flexShrink={0}>
          {hasDateRange && (
            <Text fontSize="xs" color="gray.500" fontWeight="500" mb={0} whiteSpace="nowrap">
              Between {startDate ?? "—"} {"-"} {endDate ?? "—"}
            </Text>
          )}
          {lastN !== undefined && lastN !== null && (
            <Text fontSize="xs" color="gray.500" fontWeight="500" mb={0} whiteSpace="nowrap">
              Last {lastN} runs
            </Text>
          )}
        </VStack>
      </HStack>

      {/* ── Run list ── */}
      <Box flex={1} minH={0} overflowY="auto" sx={{ scrollbarWidth: "thin" }}>
        {runs.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" h="100%" p={6}>
            <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
              No runs found.
            </Text>
          </Box>
        ) : (
          <VStack spacing={0} align="stretch">
            {runs.map((run, idx) => {
              const runId = run.header?.id;
              const runLabel = run.header?.description ?? run.header?.name ?? "Run";
              const created = run.header?.created_date;
              const timeLine =
                created !== undefined && safeTimeZone
                  ? epochConverterWithTimeZone(Number(created), "d MMM yyyy · HH:mm", safeTimeZone)
                  : "—";

              // Tally output artifact types from the full WorkflowRunProto.
              const outputs = run.output_artifacts ?? [];
              const csvCount = outputs.filter(a => a.file_artifact?.file_type === FileType.CSV).length;
              const chartCount = outputs.filter(a => a.file_artifact?.file_type === FileType.CHART_SPEC_JSON).length;
              const docCount = outputs.filter(a => a.file_artifact?.file_type === FileType.DOCX).length;
              const hasChatId = !!run.source_chat_id;

              // workflow run job id
              const jobId = run.job_id;
              const ownerId = scenarioJobs?.find(job => job.header?.id === jobId)?.header?.owner_id;
              const analystName = analystList?.find(analyst => analyst.id === ownerId)?.name;

              const authorName = runId ? runAuthorNames?.[runId] : undefined;

              const isThisLoading = !!runId && loadingRunId === runId;
              const isClickable = !!onRunClick;

              return (
                <Box
                  key={runId ?? idx}
                  px={3}
                  py={2.5}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  cursor={isClickable ? "pointer" : "default"}
                  _hover={isClickable ? { bg: "gray.50" } : {}}
                  transition="background 0.12s"
                  onClick={() => runId && onRunClick?.(runId)}
                >
                  {/* ── Row: name + right controls ── */}
                  <HStack justify="space-between" align="flex-start">
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      color="gray.800"
                      mb={0}
                      noOfLines={2}
                      textAlign="left"
                      flex={1}
                      mr={2}
                    >
                      {runLabel}
                    </Text>

                    {/* Right: time · chat icon */}
                    <HStack spacing={1.5} align="center" flexShrink={0}>
                      {isThisLoading ? (
                        <Spinner size="xs" color="purple.500" />
                      ) : (
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="500"
                          mb={0}
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                          whiteSpace="nowrap"
                        >
                          {timeLine}
                        </Text>
                      )}
                      {hasChatId && (
                        <Tooltip label="View in chat" placement="top" hasArrow openDelay={300}>
                          <Box as="span" display="inline-flex" alignItems="center">
                            <Icon
                              as={AppIcons.chatBubble}
                              boxSize={4}
                              color="gray.500"
                              flexShrink={0}
                              cursor="pointer"
                              _hover={{ color: "purple.500" }}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                window.open(`/chat/${run.source_chat_id}`, "_blank");
                              }}
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </HStack>
                  </HStack>

                  {/* ── Output type tags ── */}
                  <HStack justify="space-between" align="center">
                    {(csvCount > 0 || chartCount > 0 || docCount > 0) && (
                      <HStack spacing={1.5} mt={1.5} flexWrap="wrap">
                        {csvCount > 0 && (
                          <HStack
                            spacing={1}
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                          >
                            <Icon as={AppIcons.dataframe} boxSize={2.5} color="gray.600" />
                            <Text fontSize="xs" color="gray.600" fontWeight="500" mb={0}>
                              {csvCount} {csvCount === 1 ? "table" : "tables"}
                            </Text>
                          </HStack>
                        )}
                        {chartCount > 0 && (
                          <HStack
                            spacing={1}
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                          >
                            <Icon as={AppIcons.chart} boxSize={2.5} color="gray.600" />
                            <Text fontSize="xs" color="gray.600" fontWeight="500" mb={0}>
                              {chartCount} {chartCount === 1 ? "chart" : "charts"}
                            </Text>
                          </HStack>
                        )}
                        {docCount > 0 && (
                          <HStack
                            spacing={1}
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                          >
                            <Icon as={AppIcons.document} boxSize={2.5} color="gray.600" />
                            <Text fontSize="xs" color="gray.600" fontWeight="500" mb={0}>
                              {docCount} {docCount === 1 ? "file" : "files"}
                            </Text>
                          </HStack>
                        )}
                      </HStack>
                    )}
                    <HStack spacing={2} flexShrink={0}>
                      {authorName && (
                        <HStack spacing={1}>
                          <Icon as={AppIcons.user} boxSize={3} color="gray.500" />
                          <Text fontSize="xs" color="gray.500" fontWeight="500" mb={0} whiteSpace="nowrap">
                            {authorName}
                          </Text>
                        </HStack>
                      )}
                      {analystName && (
                        <Tooltip label={analystName} placement="top" hasArrow openDelay={300}>
                          <Box as="span" display="inline-flex" alignItems="center">
                            <Icon as={AppIcons.analyst} boxSize={4} color="purple.700" cursor="pointer" />
                          </Box>
                        </Tooltip>
                      )}
                    </HStack>
                  </HStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default WorkflowRunSearchCard;
